/**
 * Hindi for the two core namespaces (BP-19.T2).
 *
 * **Machine draft, not reviewed.** BP-19 §14's reviewer is the salon's own staff and
 * that review has not happened; `common.locale.draft` says so in the switcher. A key
 * left out here renders the English (rule 1), which is why `settings` — a staff-only
 * namespace full of statutory vocabulary — is deliberately absent rather than guessed
 * at (§11a item 4).
 *
 * `{{…}}` placeholders are i18next's and must survive translation unchanged; the
 * parity test in `locales.test.ts` fails the build if one is dropped or renamed.
 */

export const hiCommon = {
  app: {
    staff: '26AD स्टाफ',
    client: '26AD',
  },
  nav: {
    label: 'सेटिंग्स',
    tenant: 'सैलून',
    users: 'उपयोगकर्ता',
    security: 'सुरक्षा',
    logout: 'लॉग आउट',
    signIn: 'साइन इन',
    more: 'और',
    group: {
      frontDesk: 'फ्रंट डेस्क',
      salon: 'सैलून',
      business: 'व्यवसाय',
    },
    item: {
      home: 'होम',
      calendar: 'कैलेंडर',
      myDay: 'मेरा दिन',
      sales: 'बिक्री',
      clients: 'ग्राहक',
      dayClose: 'दिन का समापन',
      catalogue: 'कैटलॉग',
      services: 'सेवाएँ',
      stock: 'स्टॉक',
      team: 'टीम',
      marketing: 'मार्केटिंग',
      reports: 'रिपोर्ट',
      money: 'पैसा',
      settings: 'सेटिंग्स',
      printQueue: 'प्रिंट कतार',
      myCommission: 'मेरा कमीशन',
      commissions: 'कमीशन',
      myShifts: 'मेरी शिफ्ट',
      accounting: 'लेखा',
    },
  },
  action: {
    save: 'सहेजें',
    saving: 'सहेजा जा रहा है…',
    cancel: 'रद्द करें',
    retry: 'फिर कोशिश करें',
  },
  state: {
    loading: 'लोड हो रहा है…',
    empty: 'अभी यहाँ कुछ नहीं है।',
    error: 'कुछ गड़बड़ हो गई।',
  },
  banner: {
    readonly:
      'यह वर्कस्पेस केवल पढ़ने के लिए है। आप सब कुछ देख सकते हैं; दोबारा चालू होने तक बदलाव स्वीकार नहीं होंगे।',
    offline: 'आप ऑफ़लाइन हैं। इस डिवाइस पर सहेजा गया पिछला डेटा दिख रहा है।',
  },
  route: {
    notFound: 'पेज नहीं मिला',
    forbidden: 'इस पेज तक आपकी पहुँच नहीं है।',
  },
  locale: {
    label: 'भाषा',
    switch: 'भाषा बदलें',
    draft: 'हिन्दी, मराठी और गुजराती मशीन से बने मसौदे हैं, जिन्हें सैलून ने अभी जाँचा नहीं है।',
  },
  hello: {
    title: '26AD नमस्ते',
    loading: '26AD api से संपर्क किया जा रहा है…',
    error: '26AD api तक नहीं पहुँचा जा सका।',
  },
} as const;

export const hiAuth = {
  login: {
    title: 'साइन इन',
    email: 'ईमेल',
    password: 'पासवर्ड',
    submit: 'साइन इन करें',
    submitting: 'साइन इन हो रहा है…',
    redirecting: 'आपको साइन इन पेज पर ले जा रहे हैं…',
  },
  totp: {
    title: 'दो-चरणीय सत्यापन',
    hint: 'आपका खाता ऑथेंटिकेटर ऐप से सुरक्षित है। अभी दिख रहा छह अंकों का कोड डालें।',
    code: 'एक-बार का कोड',
    submit: 'सत्यापित करें',
  },
  otp: {
    title: 'साइन इन',
    phone: 'मोबाइल नंबर',
    phoneHint: 'अंतरराष्ट्रीय रूप में, जैसे +919820000000',
    send: 'कोड भेजें',
    sending: 'भेजा जा रहा है…',
    sent: 'हमने {{phone}} पर कोड भेजा है।',
    code: 'छह अंकों का कोड',
    verify: 'सत्यापित करें',
    verifying: 'सत्यापित हो रहा है…',
    devCode: 'डेवलपमेंट कोड: {{code}}',
    signedIn: '{{phone}} के रूप में साइन इन',
    role: 'भूमिका: ग्राहक',
  },
  error: {
    invalid: 'यह ईमेल, पासवर्ड या एक-बार का कोड सही नहीं है।',
    locked: 'बहुत बार कोशिश हुई। एक मिनट रुककर फिर कोशिश करें।',
    readonly: 'यह वर्कस्पेस केवल पढ़ने के लिए है, इसलिए अभी साइन इन से सत्र शुरू नहीं हो सकता।',
    unknown: 'साइन इन नहीं हो सका। फिर कोशिश करें।',
    phone: 'मोबाइल नंबर अंतरराष्ट्रीय रूप में डालें, जैसे +919820000000।',
    otp: 'यह कोड सही नहीं है।',
  },
} as const;

/** The namespaces Hindi carries. `settings` is absent on purpose — see the header. */
export const hi = { common: hiCommon, auth: hiAuth } as const;
